import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Two gates in one: no session → sign-in; a session whose organization has
 * not finished onboarding → its current step (and a finished one is kept
 * out of the onboarding routes). The server owns the step; this only reads
 * it.
 */
export function Gate({
  children,
  onboarding = false,
}: {
  children: ReactNode;
  onboarding?: boolean;
}) {
  const { status } = useAuth();
  const location = useLocation();
  const me = useMe();

  if (status === "loading") return <Holding label="Checking session" />;
  if (status === "signed_out")
    return (
      <Navigate to="/sign-in" state={{ from: location.pathname }} replace />
    );
  if (me.isPending) return <Holding label="Loading your workspace" />;
  if (me.isError || !me.data)
    return (
      <Holding label="Could not load your workspace. Is the API running?" />
    );

  const step = me.data.organization.onboardingStep;
  const stepPath = `/onboarding/${step}`;
  if (step !== "done" && !onboarding) return <Navigate to={stepPath} replace />;
  if (step === "done" && onboarding) return <Navigate to="/" replace />;
  if (onboarding && location.pathname !== stepPath)
    return <Navigate to={stepPath} replace />;
  return <>{children}</>;
}

function Holding({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center text-[13px] text-muted">
      {label}
    </div>
  );
}
